import React from 'react';
import { Camera, MessageSquare, CheckCircle2, Zap, ArrowRight, Phone } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from './Animations';
export const PhotoQuote = () => {
  const steps = [{
    icon: Camera,
    step: '01',
    title: 'Snap a Photo',
    desc: 'Take a quick pic of your junk pile, debris, or items.'
  }, {
    icon: MessageSquare,
    step: '02',
    title: 'Text It to Us',
    desc: 'Send to (559) 770-9442 with a short description.'
  }, {
    icon: CheckCircle2,
    step: '03',
    title: 'Get Your Quote',
    desc: 'Honest price in minutes — no obligation, no surprises.'
  }];
  return <section className="relative py-20 md:py-24 bg-gradient-to-br from-zinc-100 via-white to-zinc-100 overflow-hidden" data-id="element-407">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-red/5 rounded-full blur-3xl pointer-events-none" data-id="element-408" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-black/5 rounded-full blur-3xl pointer-events-none" data-id="element-409" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-410">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-411">
          {/* Left: Copy + Steps */}
          <div data-id="element-412">
            <FadeIn data-id="element-413">
              <div className="inline-flex items-center gap-2 bg-brand-red/10 border border-brand-red/20 text-brand-red px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-5" data-id="element-414">
                <Zap className="w-3.5 h-3.5" data-id="element-415" />
                Fastest Way to a Quote
              </div>
              <h2 className="font-oswald text-4xl md:text-5xl lg:text-6xl font-bold uppercase text-brand-black leading-[0.95] mb-5" data-id="element-416">
                Text Us a Photo. <br data-id="element-417" />
                <span className="text-brand-red" data-id="element-418">Get a Price.</span>
              </h2>
              <p className="text-zinc-600 text-lg mb-8 leading-relaxed" data-id="element-419">
                Skip the back-and-forth. Send a quick picture of what needs to
                go and we'll text you back with an honest, no-obligation quote —
                usually in minutes.
              </p>
            </FadeIn>

            <StaggerContainer className="space-y-4 mb-10" data-id="element-420">
              {steps.map((s, i) => <StaggerItem key={i} className="flex items-start gap-4 group" data-id="element-421">
                  <div className="relative shrink-0" data-id="element-422">
                    <div className="w-14 h-14 bg-white border-2 border-zinc-200 group-hover:border-brand-red rounded-sm flex items-center justify-center transition-colors shadow-sm" data-id="element-423">
                      <s.icon className="w-6 h-6 text-brand-red" data-id="element-424" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-brand-red text-white text-[10px] font-oswald font-bold flex items-center justify-center rounded-full" data-id="element-425">
                      {s.step}
                    </span>
                  </div>
                  <div className="pt-1" data-id="element-426">
                    <h3 className="font-oswald text-xl font-bold uppercase text-brand-black mb-1" data-id="element-427">
                      {s.title}
                    </h3>
                    <p className="text-zinc-600 text-sm leading-relaxed" data-id="element-428">
                      {s.desc}
                    </p>
                  </div>
                </StaggerItem>)}
            </StaggerContainer>

            <FadeIn delay={0.3} className="flex flex-col sm:flex-row gap-3" data-id="element-429">
              <a href="sms:+15597709442" className="group flex items-center justify-center gap-3 bg-brand-red hover:bg-brand-darkred text-white px-7 py-4 rounded-sm font-oswald font-bold text-lg transition-all uppercase tracking-wide shadow-xl shadow-brand-red/30 hover:scale-105" data-id="element-430">
                <MessageSquare className="w-5 h-5" data-id="element-431" />
                Text a Photo Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-432" />
              </a>
              <a href="tel:+15597709442" className="flex items-center justify-center gap-2 bg-white hover:bg-brand-black hover:text-white text-brand-black border-2 border-brand-black px-7 py-4 rounded-sm font-oswald font-bold text-lg transition-colors uppercase tracking-wide" data-id="element-433">
                <Phone className="w-5 h-5" data-id="element-434" />
                Or Call Us
              </a>
            </FadeIn>
          </div>

          {/* Right: Phone mockup with chat */}
          <FadeIn delay={0.2} className="relative flex justify-center lg:justify-end" data-id="element-435">
            {/* Decorative red glow */}
            <div className="absolute inset-0 bg-brand-red/10 blur-3xl rounded-full" data-id="element-436" />

            {/* Phone frame */}
            <div className="relative w-[300px] md:w-[340px] bg-brand-black rounded-[3rem] p-3 shadow-2xl border-[6px] border-zinc-900" data-id="element-437">
              {/* Notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-brand-black rounded-b-2xl z-20" data-id="element-438"></div>

              {/* Screen */}
              <div className="bg-zinc-100 rounded-[2.3rem] overflow-hidden h-[600px] flex flex-col" data-id="element-439">
                {/* Chat header */}
                <div className="bg-white px-4 pt-9 pb-3 border-b border-zinc-200 flex items-center gap-3" data-id="element-440">
                  <div className="w-9 h-9 bg-brand-red rounded-full flex items-center justify-center text-white font-oswald font-bold text-sm shrink-0" data-id="element-441">
                    JJ
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-442">
                    <p className="font-oswald font-bold text-brand-black text-sm leading-tight" data-id="element-443">
                      Jez Junk Away
                    </p>
                    <p className="text-[10px] text-green-600 font-semibold flex items-center gap-1" data-id="element-444">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" data-id="element-445"></span>
                      Active now
                    </p>
                  </div>
                  <Phone className="w-4 h-4 text-brand-red" data-id="element-446" />
                </div>

                {/* Chat body */}
                <div className="flex-1 p-4 space-y-3 overflow-hidden" data-id="element-447">
                  {/* Sent message - photo */}
                  <div className="flex justify-end" data-id="element-448">
                    <div className="max-w-[78%]" data-id="element-449">
                      <div className="bg-zinc-200 rounded-2xl rounded-tr-md overflow-hidden" data-id="element-450">
                        <img src="https://cdn.magicpatterns.com/uploads/poAwFT86Avp4UnkEbcNqmw/JEZ7.png" alt="" className="w-full h-32 object-cover" data-id="element-451" />
                      </div>
                      <p className="text-[9px] text-zinc-400 text-right mt-1" data-id="element-452">
                        Delivered
                      </p>
                    </div>
                  </div>

                  {/* Sent message - text */}
                  <div className="flex justify-end" data-id="element-453">
                    <div className="max-w-[78%]" data-id="element-454">
                      <div className="bg-blue-500 text-white px-3.5 py-2 rounded-2xl rounded-tr-md text-sm" data-id="element-455">
                        Hi! Can you take these recliners from Sanger?
                      </div>
                      <p className="text-[9px] text-zinc-400 text-right mt-1" data-id="element-456">
                        10:32 AM
                      </p>
                    </div>
                  </div>

                  {/* Received message */}
                  <div className="flex justify-start" data-id="element-457">
                    <div className="max-w-[78%]" data-id="element-458">
                      <div className="bg-white border border-zinc-200 px-3.5 py-2 rounded-2xl rounded-tl-md text-sm text-brand-black shadow-sm" data-id="element-459">
                        Yes! That's $120 total. We can be there tomorrow
                        morning.
                      </div>
                      <p className="text-[9px] text-zinc-500 mt-1" data-id="element-460">
                        Jez · 10:33 AM
                      </p>
                    </div>
                  </div>

                  {/* Received message - confirmation */}
                  <div className="flex justify-start" data-id="element-461">
                    <div className="max-w-[78%]" data-id="element-462">
                      <div className="bg-white border border-zinc-200 px-3.5 py-2 rounded-2xl rounded-tl-md text-sm text-brand-black shadow-sm flex items-center gap-2" data-id="element-463">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" data-id="element-464" />
                        Free estimate · No obligation
                      </div>
                    </div>
                  </div>

                  {/* Typing indicator */}
                  <div className="flex justify-end pt-2" data-id="element-465">
                    <div className="bg-blue-500 text-white px-3.5 py-2 rounded-2xl rounded-tr-md text-sm" data-id="element-466">
                      Perfect — let's do it 👍
                    </div>
                  </div>
                </div>

                {/* Input bar */}
                <div className="bg-white border-t border-zinc-200 px-3 py-2.5 flex items-center gap-2" data-id="element-467">
                  <Camera className="w-5 h-5 text-brand-red" data-id="element-468" />
                  <div className="flex-1 bg-zinc-100 rounded-full px-3 py-1.5 text-xs text-zinc-400" data-id="element-469">
                    iMessage
                  </div>
                  <div className="w-7 h-7 bg-brand-red rounded-full flex items-center justify-center" data-id="element-470">
                    <ArrowRight className="w-3.5 h-3.5 text-white" data-id="element-471" />
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge - Quote received */}
            <div className="hidden md:flex absolute top-12 -left-4 bg-white shadow-2xl rounded-sm border border-zinc-200 px-4 py-3 items-center gap-3 max-w-[200px] animate-bounce-slow" data-id="element-472">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-473">
                <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-474" />
              </div>
              <div data-id="element-475">
                <p className="font-oswald font-bold text-xs uppercase text-brand-black" data-id="element-476">
                  Quote Sent
                </p>
                <p className="text-[10px] text-zinc-500" data-id="element-477">in under 2 minutes</p>
              </div>
            </div>

            {/* Floating badge - Photo */}
            <div className="hidden md:flex absolute bottom-16 -right-4 bg-brand-red text-white shadow-2xl rounded-sm px-4 py-3 items-center gap-3" data-id="element-478">
              <Camera className="w-5 h-5 shrink-0" data-id="element-479" />
              <div data-id="element-480">
                <p className="font-oswald font-bold text-xs uppercase leading-tight" data-id="element-481">
                  1 Photo
                </p>
                <p className="text-[10px] text-red-100" data-id="element-482">= 1 fast quote</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
};